import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";

type ThemeMode = "light" | "dark" | "auto";

const THEME_STORAGE_KEY = "app-theme-mode";

const layoutConfig = reactive<{
  preset: string;
  primary: string;
  surface: string | null;
  darkTheme: boolean;
  themeMode: ThemeMode;
  menuMode: string;
  menuState: string;
  anchored: boolean;
  configSidebarVisible: boolean;
}>({
  preset: "Aura",
  primary: "blue",
  surface: null,
  darkTheme: false,
  themeMode: "light",
  menuMode: "static",
  menuState: "expanded",
  anchored: false,
  configSidebarVisible: false,
});

// System theme detection
function getSystemThemePreference(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

// Apply dark theme to DOM
function applyDarkTheme(isDark: boolean) {
  layoutConfig.darkTheme = isDark;
  if (isDark) {
    document.documentElement.classList.add("app-dark");
  } else {
    document.documentElement.classList.remove("app-dark");
  }
}

// Initialize theme based on saved preference or system
function initializeTheme() {
  const savedMode = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null;

  if (savedMode && ["light", "dark", "auto"].includes(savedMode)) {
    layoutConfig.themeMode = savedMode;
  } else {
    layoutConfig.themeMode = "light";
  }

  if (layoutConfig.themeMode === "auto") {
    applyDarkTheme(getSystemThemePreference());
  } else {
    applyDarkTheme(layoutConfig.themeMode === "dark");
  }
}

// Set theme mode and persist
function setThemeMode(mode: ThemeMode) {
  layoutConfig.themeMode = mode;
  localStorage.setItem(THEME_STORAGE_KEY, mode);

  if (mode === "auto") {
    applyDarkTheme(getSystemThemePreference());
  } else {
    applyDarkTheme(mode === "dark");
  }
}

// Handle system theme change (only applies when in auto mode)
function handleSystemThemeChange(event: MediaQueryListEvent) {
  if (layoutConfig.themeMode === "auto") {
    applyDarkTheme(event.matches);
  }
}

// Media query listener for system theme changes
let systemThemeMediaQuery: MediaQueryList | null = null;

function setupSystemThemeListener() {
  systemThemeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  systemThemeMediaQuery.addEventListener("change", handleSystemThemeChange);
}

function cleanupSystemThemeListener() {
  if (systemThemeMediaQuery) {
    systemThemeMediaQuery.removeEventListener(
      "change",
      handleSystemThemeChange,
    );
  }
}

// Export cleanup function for potential use in components that need to clean up
export { cleanupSystemThemeListener };

// Initialize theme on module load
initializeTheme();
setupSystemThemeListener();

export function useLayout() {
  const windowWidth = ref(window.innerWidth);

  const updateWindowWidth = () => {
    windowWidth.value = window.innerWidth;
  };

  onMounted(() => {
    window.addEventListener("resize", updateWindowWidth);
  });

  onUnmounted(() => {
    window.removeEventListener("resize", updateWindowWidth);
  });

  const setMenuMode = (mode: MenuMode) => {
    layoutConfig.menuMode = mode;
  };

  const setMenuState = (state: MenuState) => {
    layoutConfig.menuState = state;
    if (
      calculatedMenuMode.value === "drawer" ||
      calculatedMenuMode.value === "reveal"
    ) {
      layoutConfig.anchored = false;
    }
  };

  const toggleMenuState = () => {
    const newState =
      layoutConfig.menuState === "expanded" ? "collapsed" : "expanded";
    setMenuState(newState);
  };

  const onClose = () => {
    layoutConfig.menuState = "collapsed";
  };

  const toggleAnchored = () => {
    const newValue = !layoutConfig.anchored;
    layoutConfig.anchored = newValue;
    if (newValue) {
      layoutConfig.menuState = "expanded";
    } else {
      layoutConfig.menuState = "collapsed";
    }
  };
  const toggleConfigSidebar = () => {
    if (
      layoutConfig.menuState === "expanded" &&
      calculatedMenuMode.value === "overlay"
    ) {
      layoutConfig.menuState = "collapsed";
    }

    layoutConfig.configSidebarVisible = !layoutConfig.configSidebarVisible;
  };
  const isDarkTheme = computed(() => layoutConfig.darkTheme);
  const themeMode = computed(() => layoutConfig.themeMode);
  const isDesktop = computed(() => windowWidth.value >= 992);
  const isMobile = computed(() => windowWidth.value < 992);
  const menuMode = computed(() => layoutConfig.menuMode);
  const menuState = computed(() => layoutConfig.menuState);
  type MenuMode =
    | "static"
    | "overlay"
    | "drawer"
    | "reveal"
    | "slim"
    | "compact"
    | "horizontal";
  type MenuState = "expanded" | "collapsed";

  const calculatedMenuMode = computed<MenuMode>(() =>
    isMobile.value ? "overlay" : (layoutConfig.menuMode as MenuMode),
  );

  watch(
    calculatedMenuMode,
    (newMode: MenuMode) => {
      if (["overlay", "reveal", "drawer"].includes(newMode)) {
        setMenuState("collapsed");
      } else {
        setMenuState("expanded");
      }
    },
    { immediate: true },
  );

  return {
    layoutConfig,
    isDarkTheme,
    themeMode,
    setThemeMode,
    setMenuMode,
    setMenuState,
    toggleMenuState,
    onClose,
    toggleAnchored,
    toggleConfigSidebar,
    isDesktop,
    isMobile,
    menuMode,
    menuState,
    calculatedMenuMode,
  };
}
